import {
  formatEventDateDisplayCompact,
  getGoogleCalendarEventColor,
  normalizeStoredEventDate,
  parseEventDateList,
  parseEventDateRange,
  parseEventDateStart,
} from '@qjob/shared';
import type { Job } from '@qjob/shared';
import { getJobClientName, getJobDisplayName, getJobRowKey, getJobTypeLabel, normalizeJobStatus } from './utils';

export interface CalendarItem {
  id: string;
  type: 'job' | 'draft' | 'google';
  title: string;
  subtitle: string;
  time: string;
  status: string;
  sourceName: string;
  start: Date;
  end: Date;
  isMultiDay?: boolean;
  allDay?: boolean;
  // Exact set of selected days ("yyyy-MM-dd"), job items only — lets a job
  // booked on non-contiguous days (e.g. 1,2,3,5,10) render only on those
  // days instead of lighting up every day in [start,end].
  dateKeys?: string[];
  raw: Job | GoogleCalendarEvent;
}

export interface GoogleCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location?: string;
  description?: string;
  calendarId: string;
  calendarName: string;
}

export function getDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getJobDateRange(job: Partial<Job>): { start: Date; end: Date } | null {
  const raw = normalizeStoredEventDate(job['Event Date']);
  if (!raw) return null;
  const range = parseEventDateRange(raw);
  if (!range) return null;
  const start = new Date(range.start);
  const end = new Date(range.end);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function parseCalendarDateTime(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] || 0),
      Number(match[5] || 0),
      Number(match[6] || 0),
    );
  }
  return parseEventDateStart(raw);
}

export function formatCalendarTimeRange(start: Date, end: Date | null): string {
  if (!start) return '';
  const formatTime = (d: Date) =>
    String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  if (!end || start.getTime() === end.getTime()) return formatTime(start);
  return formatTime(start) + ' - ' + formatTime(end);
}

function normalizeCalendarText(value: unknown): string {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Whether a job item's booking actually includes the given day — checks the
// exact picked-day set (dateKeys) when present, otherwise falls back to the
// contiguous [start,end] range (yyyy-MM-dd keys sort lexically).
function jobItemCoversDateKey(jobItem: CalendarItem, dateKey: string): boolean {
  if (jobItem.dateKeys && jobItem.dateKeys.length) return jobItem.dateKeys.includes(dateKey);
  const startKey = getDateKey(jobItem.start);
  const endKey = getDateKey(jobItem.end);
  return dateKey >= startKey && dateKey <= endKey;
}

export function isDuplicateCalendarEvent(googleItem: CalendarItem, jobItems: CalendarItem[]): boolean {
  if (googleItem.type !== 'google') return false;

  // Synced job events carry JobID in the description. Hide them — the job
  // chip is the source of truth. Title matching alone fails after edit
  // (Google title is "Client - Item/Job Title", job chip is just Client).
  const jobId = extractJobIdFromGoogleEvent(googleItem.raw as GoogleCalendarEvent);
  if (jobId) {
    const hasJob = jobItems.some(
      (jobItem) => String((jobItem.raw as Job)?.JobID || '').trim() === jobId,
    );
    if (hasJob) return true;
  }

  const googleDateKey = getDateKey(googleItem.start);
  const googleTitle = normalizeCalendarText(googleItem.title);
  if (!googleDateKey || !googleTitle) return false;
  return jobItems.some((jobItem) => {
    if (!jobItemCoversDateKey(jobItem, googleDateKey)) return false;
    const jobTitle = normalizeCalendarText(jobItem.title);
    const jobSubtitle = normalizeCalendarText(jobItem.subtitle);
    return (
      (jobTitle && (googleTitle.includes(jobTitle) || jobTitle.includes(googleTitle))) ||
      (jobSubtitle && (googleTitle.includes(jobSubtitle) || jobSubtitle.includes(googleTitle)))
    );
  });
}

export function buildJobCalendarItems(jobs: Job[]): CalendarItem[] {
  return jobs
    .map((job) => {
      const range = getJobDateRange(job);
      if (!range) return null;
      const isMultiDay = getDateKey(range.start) !== getDateKey(range.end);
      const dateKeys = parseEventDateList(job['Event Date']).map((d) => getDateKey(d));
      return {
        id: 'job-' + getJobRowKey(job),
        type: 'job' as const,
        title: getJobClientName(job) || getJobDisplayName(job) || 'Job',
        subtitle: [
          formatEventDateDisplayCompact(job['Event Date']),
          getJobDisplayName(job),
          getJobTypeLabel(job) || 'Job booking',
        ]
          .filter(Boolean)
          .join(' · '),
        time: isMultiDay
          ? formatEventDateDisplayCompact(job['Event Date'])
          : String(job['Working Time'] || ''),
        status: normalizeJobStatus(job.Status),
        sourceName: 'JobBooker',
        start: range.start,
        end: range.end,
        isMultiDay,
        dateKeys,
        raw: job,
      };
    })
    .filter(Boolean) as CalendarItem[];
}

export function buildGoogleCalendarItems(events: GoogleCalendarEvent[]): CalendarItem[] {
  return events
    .map((event) => {
      const start = parseCalendarDateTime(event.start);
      let end = parseCalendarDateTime(event.end) || start;
      if (!start) return null;
      // Google all-day `end.date` is exclusive (next day). Without this the
      // same event paints an extra chip on the following day.
      if (event.allDay && end && getDateKey(end) > getDateKey(start)) {
        const adjusted = new Date(end);
        adjusted.setDate(adjusted.getDate() - 1);
        adjusted.setHours(23, 59, 59, 999);
        end = adjusted;
      }
      return {
        id: 'gcal-' + (event.id || event.calendarId + '-' + event.start + '-' + event.title),
        type: 'google' as const,
        title: event.title || 'Google Calendar event',
        subtitle: event.location || event.calendarName || 'Google Calendar',
        time: event.allDay ? 'All day' : formatCalendarTimeRange(start, end),
        status: '',
        sourceName: event.calendarName || 'Google Calendar',
        start,
        end: end || start,
        allDay: !!event.allDay,
        isMultiDay: getDateKey(start) !== getDateKey(end || start),
        raw: event,
      };
    })
    .filter(Boolean) as CalendarItem[];
}

export function extractJobIdFromGoogleEvent(event: GoogleCalendarEvent): string {
  const desc = String(event.description || '');
  const m = desc.match(/JobID:\s*(\S+)/);
  return m ? m[1] : '';
}

function calendarDescriptionField(description: string, label: string): string {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = String(description || '').match(new RegExp(`^${escaped}\\s*:\\s*(.*)$`, 'mi'));
  const value = String(match?.[1] || '').trim();
  return value === '-' ? '' : value;
}

/**
 * Rebuilds the editable job fields from a JobBooker-tagged Google event when
 * its source row is missing from Sheets. Saving the returned record lets the
 * normal save path recreate the row and re-use the same JobID/calendar event.
 */
export function recoverJobFromGoogleCalendarEvent(event: GoogleCalendarEvent): Record<string, any> | null {
  const jobId = extractJobIdFromGoogleEvent(event);
  if (!jobId) return null;
  const start = String(event.start || '').match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!start) return null;
  const eventDate = `${start[1]}-${String(start[2]).padStart(2, '0')}-${String(start[3]).padStart(2, '0')}`;
  const description = String(event.description || '');
  const price = calendarDescriptionField(description, 'ราคา/หน่วย');
  const received = calendarDescriptionField(description, 'เงินจริง');
  return {
    JobID: jobId,
    Client: calendarDescriptionField(description, 'ลูกค้า'),
    'Item / Job Title': calendarDescriptionField(description, 'รายละเอียดงาน') || String(event.title || '').split(' - ').slice(1).join(' - ').trim(),
    'Event Date': eventDate,
    'Working Time': calendarDescriptionField(description, 'เวลา'),
    'Price per Unit': price,
    'Actual Received': received,
    Status: calendarDescriptionField(description, 'สถานะ'),
    Location: String(event.location || '').trim(),
    'Google Maps Link': calendarDescriptionField(description, 'Map'),
    Notes: calendarDescriptionField(description, 'หมายเหตุ'),
    _recoveredFromCalendar: true,
  };
}

export function findJobForGoogleCalendarItem(
  event: GoogleCalendarEvent,
  jobs: Job[],
): Job | null {
  const jobId = extractJobIdFromGoogleEvent(event);
  if (jobId) {
    const byId = jobs.find((j) => String(j.JobID || '') === jobId);
    if (byId) return byId;
  }

  const googleItem = buildGoogleCalendarItems([event])[0];
  if (!googleItem) return null;
  const jobItems = buildJobCalendarItems(jobs);
  const googleDateKey = getDateKey(googleItem.start);
  const googleTitle = googleItem.title.toLowerCase().trim();

  for (const jobItem of jobItems) {
    if (!jobItemCoversDateKey(jobItem, googleDateKey)) continue;
    const jobTitle = jobItem.title.toLowerCase().trim();
    const jobSubtitle = jobItem.subtitle.toLowerCase().trim();
    if (
      (jobTitle && (googleTitle.includes(jobTitle) || jobTitle.includes(googleTitle))) ||
      (jobSubtitle && googleTitle.includes(jobSubtitle.split('·')[0]?.trim() || ''))
    ) {
      return jobItem.raw as Job;
    }
  }
  return null;
}

export function buildDraftCalendarItems(draftJobs: Job[]): CalendarItem[] {
  return buildJobCalendarItems(draftJobs).map((item) => ({
    ...item,
    id: `draft-${String((item.raw as Job)._draftId || item.id)}`,
    type: 'draft' as const,
    status: 'แบบร่าง',
    sourceName: 'แบบร่าง · เฉพาะเว็บ',
  }));
}

export function mergeCalendarItems(
  jobs: Job[],
  googleEvents: GoogleCalendarEvent[],
  draftJobs: Job[] = [],
): CalendarItem[] {
  const jobItems = buildJobCalendarItems(jobs);
  const draftItems = buildDraftCalendarItems(draftJobs);
  // Prefer work Photo (group calendar) before Gmail when collapsing dual-sync copies.
  const sortedEvents = [...googleEvents].sort((a, b) => {
    const rank = (id: string) => (/group\.calendar\.google\.com/i.test(id) ? 0 : 1);
    return rank(String(a.calendarId || '')) - rank(String(b.calendarId || ''));
  });
  const googleItems = dedupeGoogleCalendarItems(buildGoogleCalendarItems(sortedEvents));
  const visibleGoogle = googleItems.filter((item) => !isDuplicateCalendarEvent(item, jobItems));
  // Drafts are shown locally and deliberately excluded from Google-event
  // deduplication and all API sync paths.
  return jobItems.concat(draftItems, visibleGoogle).sort((a, b) => a.start.getTime() - b.start.getTime());
}

/** Collapse the same job mirrored onto multiple Google calendars into one chip. */
export function dedupeGoogleCalendarItems(items: CalendarItem[]): CalendarItem[] {
  const seenJobDay = new Set<string>();
  const seenTitleStart = new Set<string>();
  const out: CalendarItem[] = [];
  for (const item of items) {
    if (item.type !== 'google') {
      out.push(item);
      continue;
    }
    const event = item.raw as GoogleCalendarEvent;
    const jobId = extractJobIdFromGoogleEvent(event);
    const day = getDateKey(item.start);
    if (jobId) {
      const key = `${jobId}|${day}`;
      if (seenJobDay.has(key)) continue;
      seenJobDay.add(key);
      out.push(item);
      continue;
    }
    const titleKey = `${normalizeCalendarText(item.title)}|${day}|${item.time || ''}`;
    if (seenTitleStart.has(titleKey)) continue;
    seenTitleStart.add(titleKey);
    out.push(item);
  }
  return out;
}

export function getCalendarItemsForDate(items: CalendarItem[], date: Date): CalendarItem[] {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayKey = getDateKey(dayStart);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  return items.filter((item) => {
    if (item.dateKeys && item.dateKeys.length) {
      return item.dateKeys.includes(dayKey);
    }
    const start = new Date(item.start);
    const end = item.end ? new Date(item.end) : new Date(item.start);
    if (item.isMultiDay || item.allDay) {
      const itemStartDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const itemEndDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      return dayStart.getTime() >= itemStartDay.getTime() && dayStart.getTime() <= itemEndDay.getTime();
    }
    return start < dayEnd && end >= dayStart;
  });
}

export function calendarSpanRole(
  item: CalendarItem,
  date: Date,
): 'single' | 'start' | 'middle' | 'end' {
  const dayKey = getDateKey(date);
  if (item.dateKeys && item.dateKeys.length) {
    // Picked-day bookings (non-contiguous multi-date) always render as a
    // normal standalone item on every selected day — full title, no
    // connecting bar — even when two picked days happen to be adjacent.
    return 'single';
  }
  const startKey = getDateKey(item.start);
  const endKey = getDateKey(item.end);
  if (!item.isMultiDay || startKey === endKey) return 'single';
  if (dayKey === startKey) return 'start';
  if (dayKey === endKey) return 'end';
  return 'middle';
}

export function calendarSpanClass(item: CalendarItem, date: Date): string {
  const role = calendarSpanRole(item, date);
  if (role === 'start') return 'calendar-span-start';
  if (role === 'middle') return 'calendar-span-middle';
  if (role === 'end') return 'calendar-span-end';
  return 'calendar-span-single';
}

export function calendarSpanShowTitle(item: CalendarItem, date: Date): boolean {
  const role = calendarSpanRole(item, date);
  return role === 'single' || role === 'start';
}

export function calendarItemClass(item: CalendarItem): string {
  if (item.type === 'draft') return 'bg-amber-50 text-amber-900 border border-dashed border-amber-400';
  if (item.type === 'job') {
    switch (item.status) {
      case 'เสร็จสิ้น':
        return 'bg-emerald-100 text-emerald-700';
      case 'ยกเลิก':
        return 'bg-red-100 text-red-700';
      case 'รอชำระ':
      default:
        return 'bg-amber-100 text-amber-800';
    }
  }
  return 'bg-indigo-100 text-indigo-700';
}

export function calendarItemStyle(item: CalendarItem): Record<string, string> {
  if (item.type !== 'job') return {};
  const color = getGoogleCalendarEventColor((item.raw as Job)['Color']);
  return color
    ? { backgroundColor: color.background, color: color.foreground }
    : {};
}

export function calendarDayClass(day: {
  isToday: boolean;
  isCurrentMonth: boolean;
  isSelected: boolean;
}): string {
  const classes: string[] = [];
  classes.push(
    day.isCurrentMonth ? 'bg-white hover:bg-blue-50/50' : 'bg-slate-50/60 hover:bg-slate-100/80',
  );
  if (day.isSelected) classes.push('ring-2 ring-primary ring-inset bg-blue-50');
  return classes.join(' ');
}

export function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getCalendarFetchRange(cursor: Date): { start: string; end: string } {
  const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  start.setDate(start.getDate() - 7);
  const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  end.setDate(end.getDate() + 7);
  return { start: formatDateForInput(start), end: formatDateForInput(end) };
}
